import React, { useEffect, useMemo, useState } from "react";
import "./ImpactMap.css";


function formatDate(value) {
  if (!value) return null;
  const d = value?.toDate ? value.toDate() : new Date(value);
  return d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
}

function getMapStatus(sub) {
  if (sub.orderStatus === "received" || sub.latestOrder?.status === "received") return "received";
  if (sub.orderStatus === "delivered" || sub.latestOrder?.status === "delivered") return "delivered";
  if (sub.orderStatus === "shipped" || sub.latestOrder?.status === "shipped") return "shipped";
  if (sub.orderStatus === "ordered" || sub.latestOrder?.status === "ordered") return "ordered";
  if (sub.orderStatus === "needed") return "processing";
  return "scheduled";
}


// ─── Impact Map ───────────────────────────────────────────────────────────────

export default function ImpactMap({
  activeSubscriptions = [],
  donorName = "You",
  centerType = "donor",
  centerLabel,
  centerSubtext,
}) {

  const [hoveredPkg, setHoveredPkg] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [tooltipDir, setTooltipDir] = useState('below');
  const [page, setPage] = useState(0);
  const shellRef = React.useRef(null);

  const [shellSize, setShellSize] = useState({ w: 680, h: 680 });

const displayCenterLabel = centerLabel || donorName || "You";
const displayCenterSubtext =
  centerSubtext ||
  (centerType === "network" ? "Impact Network" : "somebody.gives network");


  useEffect(() => {
    if (!shellRef.current) return;
    const measure = () => {
      const { width, height } = shellRef.current.getBoundingClientRect();
      setShellSize({ w: width, h: height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(shellRef.current);
    return () => ro.disconnect();
  }, []);

  const ORGS_PER_PAGE = 6;

  const LAYOUTS = {
    1: [{ x: 50, y: 18 }],
    2: [{ x: 18, y: 18 }, { x: 82, y: 18 }],
    3: [{ x: 50, y: 18 }, { x: 85, y: 78 }, { x: 15, y: 78 }],
    4: [{ x: 50, y: 18 }, { x: 87, y: 46 }, { x: 50, y: 88 }, { x: 13, y: 46 }],
    5: [{ x: 50, y: 18 }, { x: 87, y: 36 }, { x: 76, y: 88 }, { x: 24, y: 88 }, { x: 13, y: 36 }],
    6: [{ x: 50, y: 18 }, { x: 87, y: 30 }, { x: 87, y: 74 }, { x: 50, y: 88 }, { x: 13, y: 74 }, { x: 13, y: 30 }],
  };

  const ROUTE_COLORS = ['blue', 'purple', 'green', 'orange', 'teal', 'pink'];

  const CCX_PCT = 50;
  const CCY_PCT = 50;
  const CENTER_R_PCT = 10.5;
  const CARD_HALF_PCT = 14;

  const SVG_W = shellSize.w;
  const SVG_H = shellSize.h;

  function toMs(val) {
    if (!val) return null;
    if (val?.toDate) return val.toDate().getTime();
    const t = new Date(val).getTime();
    return Number.isFinite(t) ? t : null;
  }

  function getOrderStartMs(sub) {
    return toMs(sub.latestOrder?.orderedAt)
      || toMs(sub.latestOrder?.createdAt)
      || toMs(sub.latestOrder?.billedDate)
      || toMs(sub.orderedAt)
      || toMs(sub.lastBilledDate)
      || toMs(sub.createdAt);
  }

  function getDeliveryMs(sub) {
    return toMs(sub.latestOrder?.targetDeliveryDate)
      || toMs(sub.latestOrder?.estimatedArrivalDate)
      || toMs(sub.latestOrder?.expectedDeliveryDate)
      || toMs(sub.deliveryDueDate)
      || toMs(sub.orderDueDate)
      || toMs(sub.estimatedArrivalDate)
      || toMs(sub.estimatedDeliveryDate);
  }

  function getDeliveryVal(sub) {
    return sub.latestOrder?.targetDeliveryDate
      || sub.latestOrder?.estimatedArrivalDate
      || sub.latestOrder?.expectedDeliveryDate
      || sub.deliveryDueDate
      || sub.orderDueDate
      || sub.estimatedArrivalDate
      || sub.estimatedDeliveryDate
      || null;
  }

  function getDeliveredMs(sub) {
    return toMs(sub.latestOrder?.deliveredAt)
      || toMs(sub.latestOrder?.receivedAt)
      || toMs(sub.deliveredAt)
      || toMs(sub.receivedAt);
  }

  function isFulfilled(sub) {
    const status = getMapStatus(sub);
    return status === 'delivered' || status === 'received' || !!getDeliveredMs(sub);
  }

  function isOrderOutstanding(sub) {
    const status = getMapStatus(sub);
    if (isFulfilled(sub)) return false;
    if (['processing', 'ordered', 'shipped', 'scheduled'].includes(status)) return true;
    return !!getDeliveryMs(sub);
  }

  function getPackageProgress(sub) {
    const now = Date.now();
    const startMs = getOrderStartMs(sub);
    const deliveryMs = getDeliveryMs(sub);
    const status = getMapStatus(sub);

    if (startMs && deliveryMs && deliveryMs > startMs) {
      const raw = (now - startMs) / (deliveryMs - startMs);
      return Math.max(0.06, Math.min(0.94, raw));
    }

    const fallback = {
      scheduled: 0.06,
      processing: 0.12,
      ordered: 0.28,
      shipped: 0.70,
    };

    return fallback[status] ?? 0.12;
  }

  function lerp(a, b, t) {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }

  const orgGroups = useMemo(() => {
    const g = {};
    activeSubscriptions.forEach(sub => {
      const id = sub.orgId || sub.organizationId || sub.orgName || 'unknown';
      if (!g[id]) g[id] = {
        orgId: id,
        orgName: sub.orgName || sub.organizationName || 'Organization',
        orgLogo: sub.orgLogo || sub.logoUrl || sub.logoURL || sub.organizationLogo || '',
        subscriptions: [],
      };
      if (!g[id].orgLogo) g[id].orgLogo = sub.orgLogo || sub.logoUrl || sub.logoURL || sub.organizationLogo || '';
      g[id].subscriptions.push(sub);
    });
    return Object.values(g);
  }, [activeSubscriptions]);

  const totalPages = Math.ceil(orgGroups.length / ORGS_PER_PAGE);
  const currentOrgs = orgGroups.slice(page * ORGS_PER_PAGE, (page + 1) * ORGS_PER_PAGE);
  const layout = LAYOUTS[currentOrgs.length] || LAYOUTS[6];

  const routes = useMemo(() => {
    const STATUS_PRIORITY = ['shipped', 'ordered', 'processing', 'scheduled', 'delivered', 'received'];

    return currentOrgs.map((group, i) => {
      const cardPct = layout[i];

      const dx = cardPct.x - CCX_PCT;
      const dy = cardPct.y - CCY_PCT;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = dx / len;
      const ny = dy / len;

      const cardHalfScaled = Math.min(CARD_HALF_PCT, len * 0.30);

      const lineStartPct = { x: CCX_PCT + nx * CENTER_R_PCT, y: CCY_PCT + ny * CENTER_R_PCT };
      const lineEndPct = { x: cardPct.x - nx * cardHalfScaled, y: cardPct.y - ny * cardHalfScaled };

      const lineStartPx = { x: lineStartPct.x / 100 * SVG_W, y: lineStartPct.y / 100 * SVG_H };
      const lineEndPx = { x: lineEndPct.x / 100 * SVG_W, y: lineEndPct.y / 100 * SVG_H };

      const outstandingSubs = group.subscriptions.filter(isOrderOutstanding);

      const leadOutstandingSub = [...outstandingSubs].sort((a, b) => {
        const ad = getDeliveryMs(a) || Number.MAX_SAFE_INTEGER;
        const bd = getDeliveryMs(b) || Number.MAX_SAFE_INTEGER;
        if (ad !== bd) return ad - bd;
        return STATUS_PRIORITY.indexOf(getMapStatus(a)) - STATUS_PRIORITY.indexOf(getMapStatus(b));
      })[0] || null;

      const status = leadOutstandingSub ? getMapStatus(leadOutstandingSub) : 'scheduled';
      const packageVisible = !!leadOutstandingSub;
      const t = leadOutstandingSub ? getPackageProgress(leadOutstandingSub) : null;
      const pkgPct = leadOutstandingSub ? lerp(lineStartPct, lineEndPct, t) : null;

      const statusLabel = packageVisible
        ? (status === 'processing' ? 'Processing'
          : status === 'scheduled' ? 'Scheduled'
          : status.charAt(0).toUpperCase() + status.slice(1))
        : 'Supported';

      const totalQty = group.subscriptions.reduce(
        (s, sub) => s + Number(sub.impactQuantity || sub.quantityPerCycle || sub.quantity || 0), 0
      );
      const units = [...new Set(group.subscriptions.map(s => s.impactUnit || s.unit || '').filter(Boolean))];
      const freqs = group.subscriptions.map(s => s.frequency);
      const sharedU = units.length === 1 ? units[0] : null;
      const sharedF = freqs.every(f => f === freqs[0]) ? freqs[0] : null;

      let impactSummary;
      if (totalQty > 0 && sharedU && sharedF) impactSummary = `${totalQty} ${sharedU} per cycle`;
      else if (totalQty > 0 && sharedU) impactSummary = `${totalQty} ${sharedU} supported`;
      else impactSummary = `${group.subscriptions.length} active item${group.subscriptions.length === 1 ? '' : 's'}`;

      const itemSource = packageVisible ? outstandingSubs : group.subscriptions;

      return {
        ...group,
        color: ROUTE_COLORS[i % ROUTE_COLORS.length],
        cardPct,
        lineStartPx,
        lineEndPx,
        pkgPct,
        packageVisible,
        status,
        statusLabel,
        impactSummary,
        deliveryDateVal: packageVisible ? getDeliveryVal(leadOutstandingSub) : null,
        itemNames: itemSource.map(s => s.itemName || s.needName || s.title || 'Item').filter(Boolean),
      };
    });
  }, [currentOrgs, layout, SVG_W, SVG_H]);

  const LINE_COLORS = {
    blue: '#3b82f6', purple: '#7c3aed', green: '#16a34a',
    orange: '#f59e0b', teal: '#0891b2', pink: '#db2777',
  };

  function handlePkgEnter(e, route) {
    if (!shellRef.current) return;
    const sr = shellRef.current.getBoundingClientRect();
    const pr = e.currentTarget.getBoundingClientRect();
    const cx = pr.left + pr.width / 2 - sr.left;
    const cy = pr.top + pr.height / 2 - sr.top;
    setTooltipPos({ x: cx, y: cy });
    setTooltipDir(cy > sr.height * 0.55 ? 'above' : 'below');
    setHoveredPkg(route.orgId);
  }

  function handlePkgLeave() {
    setHoveredPkg(null);
  }

  const hoveredRoute = routes.find(r => r.orgId === hoveredPkg);

  const SVG_CX = SVG_W * (CCX_PCT / 100);
  const SVG_CY = SVG_H * (CCY_PCT / 100);

  if (orgGroups.length === 0) {
    return (
      <section className="sg-impact-section">
        <div className="sg-impact-header">
          <h2>Your Impact Map</h2>
          <p>A simple view of the recurring supplies currently connected to local organizations through somebody.gives.</p>
        </div>
        <div className="sg-impact-shell">
          <div className="sg-impact-empty">
            <h3>No active impact routes yet</h3>
            <p>Once you adopt a recurring need, your impact routes will appear here.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="sg-impact-section">
      <div className="sg-impact-header">
        <h2>Your Impact Map</h2>
        <p>A simple view of the recurring supplies currently connected to local organizations through somebody.gives.</p>
      </div>

      <div className="sg-impact-shell" ref={shellRef}>
        <div className="sg-impact-canvas">
          <svg className="sg-impact-svg" viewBox={`0 0 ${SVG_W} ${SVG_H}`}>
            <circle cx={SVG_CX} cy={SVG_CY} r="80" fill="none" stroke="rgba(75,117,145,0.16)" strokeWidth="1.5" strokeDasharray="6 4" />
            <circle cx={SVG_CX} cy={SVG_CY} r="160" fill="none" stroke="rgba(75,117,145,0.13)" strokeWidth="1.5" strokeDasharray="6 4" />
            <circle cx={SVG_CX} cy={SVG_CY} r="250" fill="none" stroke="rgba(75,117,145,0.10)" strokeWidth="1.5" strokeDasharray="6 4" />

            {routes.map(route => (
              <line
                key={route.orgId}
                x1={route.lineStartPx.x} y1={route.lineStartPx.y}
                x2={route.lineEndPx.x} y2={route.lineEndPx.y}
                stroke={LINE_COLORS[route.color]}
                strokeWidth="2" strokeLinecap="round" opacity="0.7"
              />
            ))}

            <circle cx={SVG_CX} cy={SVG_CY} r="65" fill="white" stroke="rgba(107,181,238,0.50)" strokeWidth="16" />
            <circle cx={SVG_CX} cy={SVG_CY} r="47" fill="none" stroke="rgba(82,148,210,0.65)" strokeWidth="1.5" strokeDasharray="4 3" />

            <defs>
              <path id="sg-top-arc" d={`M ${SVG_CX - 50.8},${SVG_CY - 35.6} A 62,62 0 0,1 ${SVG_CX + 50.8},${SVG_CY - 35.6}`} />
              <path id="sg-bot-arc" d={`M ${SVG_CX - 42.4},${SVG_CY + 50.5} A 66,66 0 0,0 ${SVG_CX + 42.4},${SVG_CY + 50.5}`} />
            </defs>
            <text fontSize="10" fontWeight="700" fill="#1a4a7a" letterSpacing="0.3">
              <textPath href="#sg-top-arc" startOffset="50%" textAnchor="middle">somebody.gives</textPath>
            </text>
            <text fontSize="10" fontWeight="700" fill="#1a4a7a" letterSpacing="3">
              <textPath href="#sg-bot-arc" startOffset="50%" textAnchor="middle">network</textPath>
            </text>

            <circle cx={SVG_CX} cy={SVG_CY - 22} r="7" fill="#2563eb" />
            <path d={`M ${SVG_CX - 13},${SVG_CY - 2} A 13,10 0 0,1 ${SVG_CX + 13},${SVG_CY - 2} Z`} fill="#2563eb" />
            <text
  x={SVG_CX}
  y={SVG_CY+14}
  textAnchor="middle"
  fontSize="10"
  fontWeight="800"
  fill="#102844"
>
  {displayCenterLabel}
</text>

          </svg>

          {routes.filter(route => route.packageVisible && route.pkgPct).map(route => (
            <div
              key={route.orgId}
              className={`sg-pkg sg-pkg-${route.color}`}
              style={{
                left: `${route.pkgPct.x}%`,
                top: `${route.pkgPct.y}%`,
              }}
              onMouseEnter={e => handlePkgEnter(e, route)}
              onMouseLeave={handlePkgLeave}
            >
              <svg viewBox="0 0 24 24" fill="none" width="18" height="18" style={{ display: 'block', flexShrink: 0 }}>
                <rect x="3" y="8" width="18" height="13" rx="2"
                  fill="currentColor" fillOpacity="0.15"
                  stroke="currentColor" strokeWidth="1.8" />
                <line x1="3" y1="13" x2="21" y2="13" stroke="currentColor" strokeWidth="1.8" />
                <line x1="12" y1="8" x2="12" y2="5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <line x1="9" y1="5" x2="15" y2="5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
          ))}

          {hoveredPkg && hoveredRoute && (
            <div
              className={`sg-tooltip sg-tooltip-${tooltipDir}`}
              style={{ left: tooltipPos.x, top: tooltipPos.y }}
            >
              <div className="sg-tooltip-title">Items in this order</div>
              <ul className="sg-tooltip-list">
                {hoveredRoute.itemNames.map((name, i) => <li key={i}>{name}</li>)}
              </ul>
              {hoveredRoute.deliveryDateVal && (
                <div className="sg-tooltip-arrival">
                  Est. arrival: {formatDate(hoveredRoute.deliveryDateVal)}
                </div>
              )}
            </div>
          )}

          {routes.map(route => (
            <article
              key={route.orgId}
              className="sg-card"
              style={{ left: `${route.cardPct.x}%`, top: `${route.cardPct.y}%` }}
            >
              <div className="sg-card-top">
                <div className="sg-card-logo">
                  {route.orgLogo ? <img src={route.orgLogo} alt="" /> : <span>🏢</span>}
                </div>
                <div>
                  <div className="sg-card-name">{route.orgName}</div>
                  <div className="sg-card-count">{route.subscriptions.length} active item{route.subscriptions.length === 1 ? '' : 's'}</div>
                </div>
              </div>
              <span className={`sg-badge sg-badge-${route.status}`}>{route.statusLabel}</span>
              {route.deliveryDateVal && (
                <div className="sg-card-arrival">Est. arrival: {formatDate(route.deliveryDateVal)}</div>
              )}
              <div className="sg-card-summary">{route.impactSummary}</div>
            </article>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="sg-pagination">
            <button className="sg-page-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              ← Prev
            </button>
            <span className="sg-page-info">
              {page + 1} / {totalPages}
              <span className="sg-page-sub"> ({orgGroups.length} organizations)</span>
            </span>
            <button className="sg-page-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1}>
              Next →
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
